/**
 * <s-hover-gallery>: shows a product card's extra photos.
 * - Mouse: while the pointer rests on the card, cycles photos every few seconds and
 *   returns to the main photo when it leaves. With reduced motion, hover shows the
 *   second photo without auto-cycling.
 * - Touch: a horizontal swipe on the card moves to the next/previous photo (wrapping
 *   around); a tap still opens the product.
 * Extra photos live in an inert <template> and only download on first hover/touch.
 * Optional [data-gallery-dot] elements are kept in sync with the visible photo.
 */
const SWIPE_DISTANCE = 40;

class HoverGallery extends HTMLElement {
  /** @type {HTMLImageElement[]} */
  #slides = [];
  /** -1 is the main photo; 0..n-1 are the extra photos. */
  #index = -1;
  #timer = 0;
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {HTMLTemplateElement | null} */
  #template = null;
  #touchX = 0;
  #touchY = 0;
  #suppressClick = false;
  #hovering = false;
  #leaveTimer = 0;
  #firstTimer = 0;
  /** @type {number | null} */
  #pending = null;

  connectedCallback() {
    this.#template = this.querySelector(':scope > template');
    if (!this.#template) return;

    const trigger = this.closest('article') || this;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    trigger.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      window.clearTimeout(this.#leaveTimer);
      if (!this.#hovering) {
        this.#hovering = true;
        this.#startCycle();
      }
    }, { signal });
    // Brief grace period: editor overlays and nested layers can fire a leave/enter
    // flicker while the mouse never really left, which would reset the fade.
    trigger.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      window.clearTimeout(this.#leaveTimer);
      this.#leaveTimer = window.setTimeout(() => {
        this.#hovering = false;
        this.#stopCycle();
      }, 250);
    }, { signal });

    trigger.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch || !this.#isOverMedia(touch)) return;
      this.#touchX = touch.clientX;
      this.#touchY = touch.clientY;
      this.#ensureSlides();
    }, { signal, passive: true });

    trigger.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touch || this.#touchX === 0) return;
      const dx = touch.clientX - this.#touchX;
      const dy = touch.clientY - this.#touchY;
      this.#touchX = 0;
      if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.#suppressClick = true;
      window.setTimeout(() => { this.#suppressClick = false; }, 400);
      this.#step(dx < 0 ? 1 : -1);
    }, { signal, passive: true });

    // A swipe must not also open the product page.
    trigger.addEventListener('click', (event) => {
      if (!this.#suppressClick) return;
      this.#suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { signal, capture: true });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearInterval(this.#timer);
    window.clearTimeout(this.#firstTimer);
    window.clearTimeout(this.#leaveTimer);
  }

  get #interval() {
    return Number(this.getAttribute('interval')) || 2500;
  }

  /** @param {Touch} touch */
  #isOverMedia(touch) {
    const rect = this.getBoundingClientRect();
    return touch.clientX >= rect.left && touch.clientX <= rect.right
      && touch.clientY >= rect.top && touch.clientY <= rect.bottom;
  }

  #ensureSlides() {
    if (this.#slides.length === 0 && this.#template) {
      this.append(this.#template.content.cloneNode(true));
      this.#slides = Array.from(this.querySelectorAll(':scope > img[data-gallery-slide]'));
    }
    return this.#slides.length > 0;
  }

  #startCycle() {
    if (!this.#ensureSlides()) return;
    window.clearInterval(this.#timer);
    window.clearTimeout(this.#firstTimer);
    // Short pause before the first change gives the next photo time to download.
    this.#firstTimer = window.setTimeout(() => {
      if (!this.#hovering) return;
      this.#reveal(0);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.#timer = window.setInterval(() => this.#step(1), this.#interval);
    }, 600);
  }

  #stopCycle() {
    window.clearInterval(this.#timer);
    window.clearTimeout(this.#firstTimer);
    this.#pending = null;
    this.#show(-1);
  }

  /** @param {number} direction - 1 for next, -1 for previous; wraps through the main photo. */
  #step(direction) {
    if (!this.#ensureSlides()) return;
    const total = this.#slides.length + 1;
    const position = (this.#index + 1 + direction + total) % total;
    this.#reveal(position - 1);
  }

  /**
   * Shows a slide only once its photo has fully loaded, so a half-downloaded photo
   * never fades in as a see-through ghost over the main one.
   * @param {number} index
   */
  #reveal(index) {
    const slide = this.#slides[index];
    if (index === -1 || !slide || (slide.complete && slide.naturalWidth > 0)) {
      this.#pending = null;
      this.#show(index);
      return;
    }
    this.#pending = index;
    slide.addEventListener('load', () => {
      if (this.#pending === index) {
        this.#pending = null;
        this.#show(index);
      }
    }, { once: true });
  }

  /** @param {number} index - Slide to reveal; -1 shows the main photo. */
  #show(index) {
    this.#index = index;
    this.#slides.forEach((slide, i) => {
      slide.classList.toggle('opacity-0', i !== index);
    });
    this.querySelectorAll('[data-gallery-dot]').forEach((dot, i) => {
      dot.classList.toggle('opacity-40', i !== index + 1);
    });
  }
}

if (!customElements.get('s-hover-gallery')) {
  customElements.define('s-hover-gallery', HoverGallery);
}
